const HOURLY_FIELDS = {
  temperature: 'TMP',
  humidity: 'RH',
  precipitation: 'APCP',
  wind: 'WS2M',
  radiation: 'DSWRF',
  pressure: 'PRES',
};

function validDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function numberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function hourlyDateKey(value) {
  const date = validDate(value);
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function normalizeHourlyForecast(rows) {
  const normalizedRows = (rows || [])
    .map(row => {
      const timestamp = validDate(row?.datetime_utc);
      if (!timestamp) return null;

      return {
        ...row,
        timestamp,
        dayKey: hourlyDateKey(timestamp),
        temperature: numberOrNull(row[HOURLY_FIELDS.temperature]),
        humidity: numberOrNull(row[HOURLY_FIELDS.humidity]),
        precipitationAccumulated: numberOrNull(row[HOURLY_FIELDS.precipitation]) ?? 0,
        wind: numberOrNull(row[HOURLY_FIELDS.wind]),
        radiation: numberOrNull(row[HOURLY_FIELDS.radiation]),
        pressure: numberOrNull(row[HOURLY_FIELDS.pressure]),
      };
    })
    .filter(Boolean)
    .sort((first, second) => first.timestamp - second.timestamp);

  return normalizedRows.map((row, index) => {
    const previous = normalizedRows[index - 1]?.precipitationAccumulated;
    const precipitation = previous === undefined || row.precipitationAccumulated < previous
      ? row.precipitationAccumulated
      : row.precipitationAccumulated - previous;

    return { ...row, precipitation: Math.max(0, precipitation) };
  });
}

function dailyRainTotals(rows) {
  return new Map((rows || []).flatMap(row => {
    const dateKey = String(row?.date || '').match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
    const rain = numberOrNull(row?.ppt);
    return dateKey && rain !== null ? [[dateKey, Math.max(0, rain)]] : [];
  }));
}

function reconcileDailyRain(hours, dailyRain) {
  if (dailyRain === undefined || !hours.length) return hours;

  const hourlyRain = hours.reduce((total, hour) => total + hour.precipitation, 0);
  if (hourlyRain <= 0) {
    return hours.map((hour, index) => ({ ...hour, precipitation: index === 0 ? dailyRain : 0 }));
  }

  const reconciled = hours.map(hour => ({
    ...hour,
    precipitation: hour.precipitation * (dailyRain / hourlyRain),
  }));
  const reconciledTotal = reconciled.reduce((total, hour) => total + hour.precipitation, 0);
  reconciled[reconciled.length - 1].precipitation += dailyRain - reconciledTotal;
  return reconciled;
}

export function groupHourlyForecastByDay(rows, maximumDays = 4, dailyRows = []) {
  const groups = new Map();
  const rainByDay = dailyRainTotals(dailyRows);
  normalizeHourlyForecast(rows).forEach(row => {
    if (!groups.has(row.dayKey)) groups.set(row.dayKey, []);
    groups.get(row.dayKey).push(row);
  });

  return Array.from(groups, ([key, hours]) => ({
    key,
    hours: reconcileDailyRain(hours, rainByDay.get(key)),
  })).slice(0, maximumDays);
}

export function summarizeHourlyDay(hours) {
  if (!hours?.length) return null;
  const values = (field) => hours.map(hour => hour[field]).filter(value => value !== null);
  const temperatures = values('temperature');
  const humidities = values('humidity');
  const winds = values('wind');

  return {
    rain: hours.reduce((total, hour) => total + hour.precipitation, 0),
    maximumTemperature: temperatures.length ? Math.max(...temperatures) : null,
    minimumTemperature: temperatures.length ? Math.min(...temperatures) : null,
    minimumHumidity: humidities.length ? Math.min(...humidities) : null,
    maximumWind: winds.length ? Math.max(...winds) : null,
  };
}

export function formatHourlyTime(value) {
  const date = value instanceof Date ? value : validDate(value);
  return date ? date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '--:--';
}

export function formatHourlyDay(value, options = { weekday: 'short', day: '2-digit', month: '2-digit' }) {
  const date = validDate(`${value}T12:00:00`);
  return date ? date.toLocaleDateString('pt-BR', options).replace('.', '') : value;
}
